import type { Group, Object3D } from 'three';

import { ModelLoader } from './loaders';

export type AssetLoadStatus = 'loaded' | 'failed';

/**
 * What actually happened to one asset.
 *
 * This record is the reason the integration cannot quietly degrade: a fallback
 * is a field on a record that the test suite reads, not an absence of an error.
 */
export interface AssetStatus {
  name: string;
  /** Deployment-relative path, e.g. `assets/models/ball.glb`. */
  path: string;
  /** Absolute URL that was actually requested. */
  url: string;
  status: AssetLoadStatus;
  /** True when the game is rendering procedural geometry instead of this asset. */
  usedFallback: boolean;
  /** Human-readable reason, present whenever `status` is `failed`. */
  error?: string;
  /** Mesh-bearing node names found in the file, sorted. */
  nodes: string[];
}

export interface AssetReport {
  /** True only when every asset loaded and none fell back. */
  ok: boolean;
  assets: AssetStatus[];
}

export interface LoadedModel {
  name: string;
  object: Group;
  status: AssetStatus;
}

/** Collect the names of every mesh-bearing node under `root`. */
function meshNodeNames(root: Object3D): string[] {
  const names: string[] = [];
  root.traverse((child) => {
    if ((child as { isMesh?: boolean }).isMesh && child.name) names.push(child.name);
  });
  return names.sort();
}

/**
 * Loads the models the game actually renders, and keeps a per-asset record of
 * what happened.
 *
 * Two rules shape this class:
 *
 *  1. **Nothing is substituted silently.** A failed load leaves a `failed`
 *     record with the URL and the error on it. The caller may then choose to
 *     render procedural geometry, but it must call `markFallback()` to say so.
 *  2. **The game owns its own asset list.** It loads two models, not thirteen -
 *     see docs/ASSET_INTEGRATION.md §2.
 */
export class GameAssets {
  private readonly loader = new ModelLoader();
  private readonly statuses = new Map<string, AssetStatus>();

  constructor(private readonly paths: Readonly<Record<string, string>>) {}

  /** Absolute URL for a registered asset, resolved against the deployment base. */
  urlFor(name: string): string {
    const path = this.paths[name];
    if (!path) throw new Error(`GameAssets has no path registered for "${name}"`);
    return new URL(path, document.baseURI).href;
  }

  /**
   * Load one registered asset.
   *
   * Resolves to `null` on failure rather than throwing, because a missing model
   * is a recoverable condition - but it always leaves a `failed` record behind.
   */
  async load(name: string): Promise<LoadedModel | null> {
    const path = this.paths[name] ?? '';
    const url = this.urlFor(name);

    try {
      const object = await this.loader.load(url);
      const status: AssetStatus = {
        name,
        path,
        url,
        status: 'loaded',
        usedFallback: false,
        nodes: meshNodeNames(object),
      };
      this.statuses.set(name, status);
      return { name, object, status };
    } catch (error) {
      const status: AssetStatus = {
        name,
        path,
        url,
        status: 'failed',
        usedFallback: true,
        error: error instanceof Error ? `${error.name}: ${error.message}` : String(error),
        nodes: [],
      };
      this.statuses.set(name, status);
      console.error(`[roll-ball-maze] asset failed to load: ${url}`, error);
      return null;
    }
  }

  /**
   * Record that the game is rendering procedural geometry for `name`.
   *
   * Called by the game when it recovers from a failed load. Keeping this a
   * separate, explicit step is what stops a fallback from looking like a
   * successful integration.
   */
  markFallback(name: string, reason: string): void {
    const existing = this.statuses.get(name);
    const status: AssetStatus = {
      name,
      path: existing?.path ?? (this.paths[name] ?? ''),
      url: existing?.url ?? '',
      status: 'failed',
      usedFallback: true,
      error: existing?.error ?? reason,
      nodes: [],
    };
    this.statuses.set(name, status);
    console.warn(`[roll-ball-maze] falling back to procedural geometry for "${name}": ${reason}`);
  }

  statusOf(name: string): AssetStatus | undefined {
    return this.statuses.get(name);
  }

  report(): AssetReport {
    const assets = [...this.statuses.values()].sort((a, b) => a.name.localeCompare(b.name));
    return {
      ok: assets.length > 0 && assets.every((asset) => asset.status === 'loaded' && !asset.usedFallback),
      assets,
    };
  }
}

/**
 * The models the game renders, and where they live under the deployment base.
 *
 * Four models, not thirteen. The board, the ball, the machine shell and the
 * scoring row - and the last two are the ones that make the machine read as a
 * machine rather than as a board floating in space:
 *
 *  - `maze_assembly` is the whole rotating board, all six of its parts in one
 *    GLB. It is one asset because it is one rigid body.
 *  - `cabinet` is the shell: sides, marquee, apron, speakers, control panel and
 *    the LED panel the marquee display is anchored to.
 *  - `scoring_row` is the seven pockets along the bottom. It is separate from the
 *    board because it must NOT rotate with it.
 */
export const GAME_MODEL_PATHS = {
  maze_assembly: 'assets/models/maze_assembly.glb',
  ball: 'assets/models/ball.glb',
  cabinet: 'assets/models/cabinet.glb',
  scoring_row: 'assets/models/scoring_row.glb',
} as const;
